import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { UserPublic, Organization, Invite } from '../../types/auth';
import {
  getUsers,
  getOrganizations,
  createOrganization,
  createUser,
  resetUserPassword,
  getInvites,
  sendInvite,
  revokeInvite,
  deactivateUser,
  activateUser,
  impersonateUser,
} from '../../services/auth';
import { HQ_ORG_ID } from '../../utils/access';

// 14-char alphanumeric. Avoids visually confusable chars (0/O/1/l/I) so
// a customer reading it from a chat message or screen-share gets it
// right the first time. Mirrors the server-side default — both ends
// can generate if needed.
function generateRandomPassword(length = 14): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < length; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

async function copyToClipboard(text: string): Promise<void> {
  try { await navigator.clipboard.writeText(text); } catch { /* noop */ }
}

export default function UserManagement() {
  const { user: currentUser, getToken, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

  const [users, setUsers] = useState<UserPublic[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);

  // Org name for the Users table. `orgs` is only fetched for super_admins
  // (org_admins can't list orgs), which is fine — the column is super_admin
  // only. Falls back to the raw id so a user whose org was deleted still
  // renders something you can grep for rather than a blank cell.
  const orgName = (orgId: string | null): string => {
    if (!orgId) {return '—';}
    return orgs.find(o => o.id === orgId)?.name || orgId;
  };

  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);

  // Invite form
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [inviteOrgId, setInviteOrgId] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  // Org form
  const [showOrgForm, setShowOrgForm] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');

  // Create User form (direct credential creation, no email invite)
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [cuEmail, setCuEmail] = useState('');
  const [cuFirstName, setCuFirstName] = useState('');
  const [cuLastName, setCuLastName] = useState('');
  const [cuRole, setCuRole] = useState<'user' | 'org_admin'>('user');
  const [cuOrgId, setCuOrgId] = useState('');
  const [cuPassword, setCuPassword] = useState(() => generateRandomPassword());
  const [cuShowPassword, setCuShowPassword] = useState(false);
  const [cuError, setCuError] = useState('');
  const [cuCreated, setCuCreated] = useState<{ email: string; password: string; firstName?: string; lastName?: string } | null>(null);

  // Per-row password reset display (shown inline below the row that was reset)
  const [resetForUserId, setResetForUserId] = useState<string | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState<string>('');

  const [activeTab, setActiveTab] = useState<'users' | 'invites' | 'orgs'>('users');

  const loadData = useCallback(async () => {
    const token = await getToken();
    if (!token) {return;}
    setLoading(true);
    try {
      const [u, inv] = await Promise.all([
        getUsers(token),
        getInvites(token),
      ]);
      setUsers(u);
      setInvites(inv);

      if (isSuperAdmin) {
        const o = await getOrganizations(token);
        setOrgs(o);
      }
    } catch (err) {
      console.error('Failed to load admin data:', err);
    } finally {
      setLoading(false);
    }
  }, [getToken, isSuperAdmin]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');

    try {
      const token = await getToken();
      if (!token) { setInviteError('Your session expired. Please sign in again.'); return; }
      const invite = await sendInvite(inviteEmail, inviteRole, token, inviteOrgId || undefined);
      setInviteSuccess(`Invite sent! Link: ${invite.inviteUrl}`);
      setInviteEmail('');
      setInvites(prev => [invite, ...prev]);
    } catch (err: any) {
      setInviteError(err.response?.data?.error || 'Failed to send invite');
    }
  }

  async function handleCreateOrg(e: React.FormEvent) {
    e.preventDefault();
    if (!newOrgName.trim()) {return;}

    try {
      const token = await getToken();
      if (!token) {return;}
      const org = await createOrganization(newOrgName, token);
      setOrgs(prev => [org, ...prev]);
      setNewOrgName('');
      setShowOrgForm(false);
    } catch (err: any) {
      console.error('Failed to create org:', err);
    }
  }

  function openCreateUserForm() {
    setCuEmail('');
    setCuFirstName('');
    setCuLastName('');
    setCuRole('user');
    setCuOrgId(isSuperAdmin ? '' : (currentUser?.orgId || ''));
    setCuPassword(generateRandomPassword());
    setCuShowPassword(false);
    setCuError('');
    setCuCreated(null);
    setShowCreateUserForm(true);
  }

  async function handleCreateUser(e: React.FormEvent) {
    e.preventDefault();
    setCuError('');

    const email = cuEmail.trim().toLowerCase();
    if (!email) { setCuError('Email is required'); return; }
    if (cuPassword.length < 8) { setCuError('Password must be at least 8 characters'); return; }
    const targetOrgId = isSuperAdmin ? cuOrgId : (currentUser?.orgId || '');
    if (!targetOrgId) { setCuError('Organization is required'); return; }

    try {
      const token = await getToken();
      if (!token) { setCuError('Your session expired. Please sign in again.'); return; }
      const created = await createUser({
        email,
        password: cuPassword,
        firstName: cuFirstName.trim() || undefined,
        lastName: cuLastName.trim() || undefined,
        role: cuRole,
        orgId: targetOrgId,
      }, token);
      setCuCreated({
        email: created.email,
        password: cuPassword,
        firstName: created.firstName ?? undefined,
        lastName: created.lastName ?? undefined,
      });
      // Refresh the users list so the new row appears
      const u = await getUsers(token);
      setUsers(u);
    } catch (err: any) {
      setCuError(err.response?.data?.error || 'Failed to create user');
    }
  }

  async function handleResetPassword(userId: string) {
    if (!window.confirm('Generate a new password for this user? Their current password will stop working immediately.')) {return;}
    try {
      const token = await getToken();
      if (!token) { window.alert('Your session expired. Please sign in again.'); return; }
      const { password } = await resetUserPassword(userId, token);
      setResetForUserId(userId);
      setResetPasswordValue(password);
    } catch (err: any) {
      window.alert(err.response?.data?.error || 'Failed to reset password');
    }
  }

  // "Log in as customer". Swaps this browser into the target user's session
  // and drops you on their dashboard. You come back via the banner at the top
  // of the screen. Full access — see the note in backend admin.ts.
  async function handleImpersonate(u: UserPublic) {
    const who = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
    if (!window.confirm(
      `Log in as ${who} (${u.email})?\n\n` +
      `You will see the app exactly as they do, with their permissions. ` +
      `Anything you change will look like they changed it. ` +
      `Use the banner at the top to return to your own account.`
    )) {return;}

    setImpersonatingId(u.id);
    try {
      const token = await getToken();
      if (!token) { window.alert('Your session expired. Please sign in again.'); return; }
      const { user, organization, tokens } = await impersonateUser(u.id, token);
      startImpersonation(user, organization, tokens);
      void navigate('/');
    } catch (err: any) {
      window.alert(err.response?.data?.error || 'Failed to log in as this user');
    } finally {
      setImpersonatingId(null);
    }
  }

  async function handleToggleUser(userId: string, isActive: boolean) {
    try {
      const token = await getToken();
      if (!token) {return;}
      if (isActive) {
        await deactivateUser(userId, token);
      } else {
        await activateUser(userId, token);
      }
      await loadData();
    } catch (err: any) {
      console.error('Failed to toggle user:', err);
    }
  }

  async function handleRevokeInvite(inviteId: string) {
    try {
      const token = await getToken();
      if (!token) {return;}
      await revokeInvite(inviteId, token);
      setInvites(prev => prev.filter(i => i.id !== inviteId));
    } catch (err: any) {
      console.error('Failed to revoke invite:', err);
    }
  }

  if (loading) {
    return <div className="main-content"><p>Loading...</p></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">User Management</h1>
          <p className="page-subtitle">
            {isSuperAdmin ? 'Manage all organizations, users, and invites' : 'Manage users in your organization'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={openCreateUserForm}>
            + Create User
          </button>
          <button className="btn btn-primary" onClick={() => setShowInviteForm(true)}>
            + Invite User
          </button>
        </div>
      </div>

      {/* Create User Modal (direct credentials, no email) */}
      {showCreateUserForm && (
        <div className="modal-overlay" onClick={() => setShowCreateUserForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            {!cuCreated ? (
              <>
                <h2 className="modal-title">Create User</h2>
                <p style={{ fontSize: 13, color: '#6b7280', marginTop: -8, marginBottom: 16 }}>
                  Skip the email invite — set credentials directly and hand them to the user yourself.
                </p>
                {cuError && <div className="login-error">{cuError}</div>}
                <form onSubmit={handleCreateUser}>
                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      className="form-input"
                      value={cuEmail}
                      onChange={(e) => setCuEmail(e.target.value)}
                      placeholder="user@company.com"
                      required
                      autoFocus
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="form-label">First name (optional)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={cuFirstName}
                        onChange={(e) => setCuFirstName(e.target.value)}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="form-label">Last name (optional)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={cuLastName}
                        onChange={(e) => setCuLastName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={cuRole}
                      onChange={(e) => setCuRole(e.target.value as 'user' | 'org_admin')}
                    >
                      <option value="user">User</option>
                      <option value="org_admin">Organization Admin</option>
                    </select>
                  </div>
                  {isSuperAdmin && (
                    <div className="form-group">
                      <label className="form-label">Organization</label>
                      <select
                        className="form-select"
                        value={cuOrgId}
                        onChange={(e) => setCuOrgId(e.target.value)}
                        required
                      >
                        <option value="">Select organization...</option>
                        {orgs.map(org => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type={cuShowPassword ? 'text' : 'password'}
                        className="form-input"
                        value={cuPassword}
                        onChange={(e) => setCuPassword(e.target.value)}
                        style={{ flex: 1, fontFamily: cuShowPassword ? 'monospace' : 'inherit' }}
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setCuShowPassword(s => !s)}
                        title={cuShowPassword ? 'Hide' : 'Show'}
                        style={{ width: 60 }}
                      >
                        {cuShowPassword ? 'Hide' : 'Show'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => { setCuPassword(generateRandomPassword()); setCuShowPassword(true); }}
                        title="Generate a new random password"
                      >
                        Regenerate
                      </button>
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                      Minimum 8 characters. Default is auto-generated; click Regenerate for a new one.
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowCreateUserForm(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Create User</button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h2 className="modal-title">User Created</h2>
                <p style={{ fontSize: 13, color: '#6b7280', marginTop: -8, marginBottom: 16 }}>
                  Hand these credentials to the user. The password is shown ONLY HERE — copy it now.
                </p>
                <div style={{ background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16, fontSize: 14 }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Email</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                      <span style={{ fontFamily: 'monospace', flex: 1 }}>{cuCreated.email}</span>
                      <button className="btn btn-secondary btn-sm" onClick={() => copyToClipboard(cuCreated.email)}>Copy</button>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>Password</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                      <span style={{ fontFamily: 'monospace', flex: 1 }}>{cuCreated.password}</span>
                      <button className="btn btn-secondary btn-sm" onClick={() => copyToClipboard(cuCreated.password)}>Copy</button>
                    </div>
                  </div>
                </div>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => copyToClipboard(`Email: ${cuCreated.email}\nPassword: ${cuCreated.password}\nLogin: ${window.location.origin}/login`)}
                  style={{ marginBottom: 12 }}
                >
                  Copy email + password + login URL together
                </button>
                <div className="modal-actions">
                  <button type="button" className="btn btn-primary" onClick={() => setShowCreateUserForm(false)}>Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Invite Form Modal */}
      {showInviteForm && (
        <div className="modal-overlay" onClick={() => setShowInviteForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Invite User</h2>
            {inviteError && <div className="login-error">{inviteError}</div>}
            {inviteSuccess && <div className="invite-success">{inviteSuccess}</div>}
            <form onSubmit={handleInvite}>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-input"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@company.com"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-select" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                  <option value="user">User</option>
                  <option value="org_admin">Organization Admin</option>
                </select>
              </div>
              {isSuperAdmin && orgs.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Organization</label>
                  <select className="form-select" value={inviteOrgId} onChange={(e) => setInviteOrgId(e.target.value)}>
                    <option value="">Select organization...</option>
                    {orgs.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowInviteForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Send Invite</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <button className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
          Users ({users.length})
        </button>
        <button className={`admin-tab ${activeTab === 'invites' ? 'active' : ''}`} onClick={() => setActiveTab('invites')}>
          Pending Invites ({invites.filter(i => !i.acceptedAt).length})
        </button>
        {isSuperAdmin && (
          <button className={`admin-tab ${activeTab === 'orgs' ? 'active' : ''}`} onClick={() => setActiveTab('orgs')}>
            Organizations ({orgs.length})
          </button>
        )}
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                {isSuperAdmin && <th>Organization</th>}
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className={!u.isActive ? 'inactive-row' : ''}>
                  <td>{[u.firstName, u.lastName].filter(Boolean).join(' ') || 'N/A'}</td>
                  <td>{u.email}</td>
                  {isSuperAdmin && (
                    <td>
                      {orgName(u.orgId)}
                      {u.orgId === HQ_ORG_ID && (
                        <span
                          title="Data Bodies HQ — this org can see the Identity Graph"
                          style={{
                            marginLeft: 6,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            color: '#6366f1',
                            border: '1px solid #c7d2fe',
                            background: '#eef2ff',
                            borderRadius: 4,
                            padding: '1px 5px',
                            verticalAlign: 'middle',
                          }}
                        >
                          HQ
                        </span>
                      )}
                    </td>
                  )}
                  <td>
                    <span className={`role-badge role-${u.role}`}>
                      {u.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    <span className={`card-status ${u.isActive ? 'active' : 'archived'}`}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      {/* Log in as customer — super_admin only. Hidden for
                          yourself, for other super_admins (backend refuses
                          both), and for deactivated users (nothing to see). */}
                      {isSuperAdmin
                        && u.id !== currentUser?.id
                        && u.role !== 'super_admin'
                        && u.isActive && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleImpersonate(u)}
                          disabled={impersonatingId === u.id}
                          title={`Log in as ${u.email}`}
                          aria-label={`Log in as ${u.email}`}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                            <polyline points="10 17 15 12 10 7" />
                            <line x1="15" y1="12" x2="3" y2="12" />
                          </svg>
                          {impersonatingId === u.id ? 'Entering…' : 'Log in as'}
                        </button>
                      )}
                      {u.id !== currentUser?.id && (
                        <button
                          className={`btn btn-sm ${u.isActive ? 'btn-danger' : 'btn-primary'}`}
                          onClick={() => handleToggleUser(u.id, u.isActive)}
                        >
                          {u.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleResetPassword(u.id)}
                        title="Generate a new password for this user"
                      >
                        Reset Password
                      </button>
                    </div>
                    {resetForUserId === u.id && resetPasswordValue && (
                      <div style={{ marginTop: 8, padding: 10, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 13 }}>
                        <div style={{ fontSize: 11, color: '#92400e', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em', marginBottom: 4 }}>
                          New Password (shown once — copy now)
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontFamily: 'monospace', flex: 1 }}>{resetPasswordValue}</span>
                          <button className="btn btn-secondary btn-sm" onClick={() => copyToClipboard(resetPasswordValue)}>Copy</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => { setResetForUserId(null); setResetPasswordValue(''); }}>Dismiss</button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invites Tab */}
      {activeTab === 'invites' && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Sent</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map(inv => (
                <tr key={inv.id}>
                  <td>{inv.email}</td>
                  <td>
                    <span className={`role-badge role-${inv.role}`}>
                      {inv.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    <span className={`card-status ${inv.acceptedAt ? 'active' : 'paused'}`}>
                      {inv.acceptedAt ? 'Accepted' : 'Pending'}
                    </span>
                  </td>
                  <td>{new Date(inv.createdAt).toLocaleDateString()}</td>
                  <td>{new Date(inv.expiresAt).toLocaleDateString()}</td>
                  <td>
                    {!inv.acceptedAt && (
                      <button className="btn btn-sm btn-danger" onClick={() => handleRevokeInvite(inv.id)}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {invites.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>No invites yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Organizations Tab (super_admin only) */}
      {activeTab === 'orgs' && isSuperAdmin && (
        <div>
          <div style={{ marginBottom: '16px' }}>
            {showOrgForm ? (
              <form onSubmit={handleCreateOrg} style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                  <input
                    type="text"
                    className="form-input"
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    placeholder="Organization name"
                    required
                    autoFocus
                  />
                </div>
                <button type="submit" className="btn btn-primary">Create</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowOrgForm(false)}>Cancel</button>
              </form>
            ) : (
              <button className="btn btn-secondary" onClick={() => setShowOrgForm(true)}>+ New Organization</button>
            )}
          </div>
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map(org => (
                  <tr key={org.id}>
                    <td style={{ fontWeight: 600 }}>{org.name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{org.slug}</td>
                    <td>
                      <span className={`card-status ${org.isActive ? 'active' : 'archived'}`}>
                        {org.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{new Date(org.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
